/**
 * Global streaming store - persists across navigation
 * Handles streaming state for live execution bubbles
 */

import { create } from "zustand";
import { type Bubble, type StreamAction, streamReducer } from "@/lib/streaming/reducer";
import { deriveSnapshotRegistry } from "@/lib/streaming/shared";

interface RunMetadata {
	runId?: number;
	agentName?: string;
	runUuid?: string;
	instanceId?: number;
	taskId?: number;
	status?: string;
}

export interface StreamingState {
	// State
	bubbles: Bubble[];
	currentBubble: Bubble | null;
	currentBubbleIndex: number | null;
	toolRegistry: Map<string, Bubble>;
	blockRegistry: Map<string, number>;
	snapshotRegistry: Set<string>;
	localRunIds: Set<number>;
	runMetadata: RunMetadata;
	isStreaming: boolean;
	error: Error | null;

	// Actions
	dispatch: (action: StreamAction) => void;
	setStreaming: (isStreaming: boolean) => void;
	setError: (error: Error | null) => void;
	setRunMetadata: (metadata: RunMetadata) => void;
	reset: () => void;
	hydrate: (payload: { bubbles: Bubble[]; runMetadata?: RunMetadata }) => void;
	addLocalRun: (runId: number) => void;
	removeLocalRun: (runId: number) => void;
}

// Initial state
const initialState = {
	bubbles: [],
	currentBubble: null,
	currentBubbleIndex: null,
	toolRegistry: new Map<string, Bubble>(),
	blockRegistry: new Map<string, number>(),
	snapshotRegistry: new Set<string>(),
	localRunIds: new Set<number>(),
	runMetadata: {},
	isStreaming: false,
	error: null,
};

export const useStreamingStore = create<StreamingState>((set, get) => ({
	...initialState,

	// Dispatch reducer action
	dispatch: (action: StreamAction) => {
		const state = get();
		const reducerState = {
			bubbles: state.bubbles,
			currentBubble: state.currentBubble,
			currentBubbleIndex: state.currentBubbleIndex,
			toolRegistry: state.toolRegistry,
			blockRegistry: state.blockRegistry,
			snapshotRegistry: state.snapshotRegistry,
			runMetadata: state.runMetadata,
		};

		const newState = streamReducer(reducerState, action);

		set({
			bubbles: newState.bubbles,
			currentBubble: newState.currentBubble,
			currentBubbleIndex: newState.currentBubbleIndex,
			toolRegistry: newState.toolRegistry,
			blockRegistry: newState.blockRegistry,
			snapshotRegistry: newState.snapshotRegistry,
			runMetadata: newState.runMetadata,
		});
	},

	// Set streaming state
	setStreaming: (isStreaming: boolean) => {
		set({ isStreaming });
	},

	// Set error state
	setError: (error: Error | null) => {
		set({ error });
	},

	setRunMetadata: (metadata: RunMetadata) => {
		set((state) => ({
			runMetadata: {
				...state.runMetadata,
				...metadata,
			},
		}));
	},

	// Reset to initial state
	reset: () => {
		set({
			...initialState,
			toolRegistry: new Map<string, Bubble>(),
			blockRegistry: new Map<string, number>(),
			snapshotRegistry: new Set<string>(),
			localRunIds: new Set<number>(),
		});
	},

	// Hydrate state from historical bubbles
	hydrate: ({ bubbles, runMetadata }) => {
		const snapshotRegistry = deriveSnapshotRegistry(bubbles);
		set({
			bubbles: bubbles.map((bubble) => ({
				...bubble,
				metadata: bubble.metadata ? { ...bubble.metadata } : undefined,
			})),
			currentBubble: null,
			currentBubbleIndex: null,
			toolRegistry: new Map<string, Bubble>(),
			blockRegistry: new Map<string, number>(),
			snapshotRegistry,
			localRunIds: new Set<number>(),
			runMetadata: {
				...initialState.runMetadata,
				...runMetadata,
			},
		});
	},
	addLocalRun: (runId: number) => {
		set((state) => {
			if (state.localRunIds.has(runId)) {
				return {};
			}
			const next = new Set(state.localRunIds);
			next.add(runId);
			return { localRunIds: next };
		});
	},
	removeLocalRun: (runId: number) => {
		set((state) => {
			if (!state.localRunIds.has(runId)) {
				return {};
			}
			const next = new Set(state.localRunIds);
			next.delete(runId);
			return { localRunIds: next };
		});
	},
}));
